import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, LifeBuoy, MessageSquare, History, Plus, Loader2,
  Clock, X, Tag, AlertTriangle, Paperclip, Send,
  Download, User, CheckCircle
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import Button from '../../components/Common/Button';

// ─── Config ─────────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  open: { label: 'Open', color: 'bg-amber-500/10 text-amber-500 border-amber-500/20' },
  'in-progress': { label: 'In Progress', color: 'bg-blue-500/10 text-blue-500 border-blue-500/20' },
  resolved: { label: 'Resolved', color: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' },
  closed: { label: 'Closed', color: 'bg-slate-500/10 text-slate-500 border-slate-500/20' },
};

const CATEGORIES = [
  { value: 'technical', label: 'Technical Issue', description: 'Hardware, software, device connection, system bugs' },
  { value: 'account', label: 'Account Issue', description: 'Login, access, permissions' },
  { value: 'course', label: 'Course Issue', description: 'Course content, videos, modules access' },
  { value: 'assessment', label: 'Assessment Issue', description: 'Test access, submission errors, grading' },
  { value: 'career Direction', label: 'Career Direction Issue', description: 'Career Direction guidance and support' },
  { value: 'placement issue', label: 'Placement Issue', description: 'Job application, placement drive, resume upload' },
  { value: 'certificates & badges issue', label: 'Certificates & Badges Issue', description: 'Certificate generation, badge unlock, sharing' },
  { value: 'other', label: 'Others Issue', description: 'General IT inquiries' },
];

const PRIORITIES = [
  { value: 'low', label: 'Low', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
  { value: 'medium', label: 'Medium', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
  { value: 'high', label: 'High', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
];

// ─── TicketDetail Modal ──────────────────────────────────────────────────────
const TicketDetailModal = ({ ticket: initialTicket, onClose, onUpdate }) => {
  const [ticket, setTicket] = useState(initialTicket);
  const [loading, setLoading] = useState(true);
  const [replyMessage, setReplyMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const getAttachmentUrl = (file) => {
    if (!file) return '#';
    
    // 1. If it's an HTTP/S link already, return it directly
    const url = file.url || file.publicUrl;
    if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
      return url;
    }
    if (file.path && (file.path.startsWith('http://') || file.path.startsWith('https://'))) {
      return file.path;
    }
    
    // 2. Extract the file name
    let filename = file.filename;
    if (!filename && file.path) {
      filename = file.path.split(/[/\\]/).pop();
    }
    
    if (!filename) return '#';
    
    // 3. Determine base URL (student tickets are uploaded to the student backend)
    const isStudentTicket = ticket?.userModel === 'Student' || 
                            ticket?.user?.role === 'student' || 
                            (ticket?.userId && ticket?.userId.role === 'student');
    
    const backendBaseUrl = isStudentTicket
      ? (process.env.REACT_APP_STUDENT_API_URL || `http://${window.location.hostname}:5000`)
      : (process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5001`);
        
    const cleanBaseUrl = backendBaseUrl.endsWith('/') ? backendBaseUrl.slice(0, -1) : backendBaseUrl;
    return `${cleanBaseUrl}/uploads/${filename}`;
  };

  // Fetch full ticket data when modal opens
  useEffect(() => {
    const fetchTicketDetail = async () => {
      try {
        const { data } = await api.get(`/tickets/${initialTicket._id}`);
        if (data.success) {
          setTicket(data.data);
        }
      } catch (err) {
        if (!navigator.onLine) {
          toast.error('No internet connection. Please check your network and try again.');
        } else {
          console.error('Failed to fetch ticket details:', err);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchTicketDetail();
  }, [initialTicket._id]);

  const statusCfg = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.open;

  const formatDate = (d) => d ? new Date(d).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  }) : '—';

  const handleReply = async () => {
    if (!replyMessage.trim()) return;
    setIsSubmitting(true);
    try {
      const { data } = await api.post(`/tickets/${ticket._id}/response`, {
        message: replyMessage.trim()
      });
      if (data.success) {
        setSubmitSuccess(true);
        setReplyMessage('');
        setTicket(data.data); // Update local ticket state
        setTimeout(() => {
          setSubmitSuccess(false);
          if (onUpdate) onUpdate(data.data);
        }, 1500);
      }
    } catch (err) {
      if (!navigator.onLine) {
        toast.error('No internet connection. Please check your network and try again.');
      } else {
        toast.error('Failed to send reply');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-2xl bg-white dark:bg-[#001229] border border-slate-200 dark:border-[#1a3884]/30 flex flex-col mx-2"
      >
        {/* Header */}
        <div className="p-4 sm:p-6 border-b border-slate-100 dark:border-[#1a3884]/20 bg-slate-50/50 dark:bg-[#001f40]/45">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className="text-sm text-slate-400 dark:text-gray-500 font-mono">
                  #{ticket.ticketId || ticket._id?.slice(-6)}
                </span>
                <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest rounded-full border ${statusCfg.color}`}>
                  {statusCfg.label}
                </span>
                {ticket.itsmTicketNumber && (
                  <span className="px-2 py-0.5 text-xs rounded-full border bg-[#1a3884]/20 text-[#1a3884] dark:text-[#4f7dff] border-[#1a3884]/30 font-mono flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#1a3884] dark:bg-[#4f7dff] inline-block" />
                    {ticket.itsmTicketNumber}
                  </span>
                )}
              </div>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white tracking-tight">
                {ticket.subject || ticket.title}
              </h2>
            </div>
            <button onClick={onClose} className="p-2 rounded-lg text-slate-400 dark:text-gray-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/5 transition-colors flex-shrink-0">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#4f7dff] mb-3" />
              <p className="text-slate-400 dark:text-gray-400 text-sm">Loading ticket details...</p>
            </div>
          ) : (
            <>
              {/* Meta */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-slate-700 dark:text-gray-300">
                <div className="p-3 rounded-xl bg-slate-50 dark:bg-[#002147] border border-slate-100 dark:border-[#1a3884]/20">
                  <div className="flex items-center gap-2 text-slate-400 dark:text-gray-500 text-xs mb-1"><Tag className="w-3 h-3" />Category</div>
                  <span className="text-slate-900 dark:text-white text-sm font-semibold capitalize">{ticket.category || '—'}</span>
                </div>
                <div className="p-3 rounded-xl bg-slate-50 dark:bg-[#002147] border border-slate-100 dark:border-[#1a3884]/20">
                  <div className="flex items-center gap-2 text-slate-400 dark:text-gray-500 text-xs mb-1"><Clock className="w-3 h-3" />Created</div>
                  <span className="text-slate-900 dark:text-white text-sm font-semibold">{formatDate(ticket.createdAt)}</span>
                </div>
                <div className="p-3 rounded-xl bg-slate-50 dark:bg-[#002147] border border-slate-100 dark:border-[#1a3884]/20">
                  <div className="flex items-center gap-2 text-slate-400 dark:text-gray-500 text-xs mb-1"><User className="w-3 h-3" />Assigned To</div>
                  <span className="text-slate-900 dark:text-white text-sm font-semibold">
                    {ticket.assignedTo?.fullName || 'Unassigned'}
                  </span>
                </div>
              </div>

              {/* Description */}
              <div>
                <h3 className="text-xs font-bold text-slate-400 dark:text-gray-500 uppercase tracking-widest pl-1 mb-2">Description</h3>
                <div className="p-4 rounded-xl bg-slate-50 dark:bg-[#002147] border border-slate-100 dark:border-[#1a3884]/20">
                  <p className="text-slate-700 dark:text-gray-200 whitespace-pre-wrap text-sm leading-relaxed">{ticket.description}</p>
                </div>
              </div>

              {/* Attachments */}
              {ticket.attachments?.length > 0 && (
                <div>
                  <h3 className="text-xs font-bold text-slate-400 dark:text-gray-500 uppercase tracking-widest pl-1 mb-2 flex items-center gap-2">
                    <Paperclip className="w-4 h-4" /> Attachments ({ticket.attachments.length})
                  </h3>
                  <div className="flex flex-wrap gap-3">
                    {ticket.attachments.map((file, i) => {
                      const fileUrl = getAttachmentUrl(file);
                      const isImage = file.mimetype?.startsWith('image/') || 
                                      /\.(jpg|jpeg|png|gif|webp)$/i.test(fileUrl || file.originalName || file.name || file.filename || '');
                      return (
                        <div key={i} className="flex flex-col gap-1">
                          {isImage ? (
                            <a href={fileUrl}
                              target="_blank" rel="noopener noreferrer"
                              className="block w-24 h-24 rounded-lg overflow-hidden border border-slate-200 dark:border-[#1a3884]/30 hover:border-[#4f7dff] transition-all shadow-sm group relative"
                            >
                              <img src={fileUrl}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                alt={file.originalName || file.name || file.filename || 'Attachment'}
                              />
                              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                                <span className="text-[10px] text-white font-medium bg-black/60 px-2 py-0.5 rounded">View</span>
                              </div>
                            </a>
                          ) : (
                            <a href={fileUrl}
                              target="_blank" rel="noopener noreferrer"
                              download={file.originalName || file.name || file.filename}
                              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-50 dark:bg-[#002147] border border-slate-200 dark:border-[#1a3884]/30 hover:border-[#4f7dff] transition-colors group"
                            >
                              <Paperclip className="w-4 h-4 text-[#4f7dff]" />
                              <span className="text-sm text-slate-700 dark:text-gray-300 max-w-[150px] truncate">{file.originalName || file.name || file.filename}</span>
                              <Download className="w-4 h-4 text-gray-500 group-hover:text-[#4f7dff] transition-colors" />
                            </a>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Responses */}
              <div>
                <h3 className="text-xs font-bold text-slate-400 dark:text-gray-500 uppercase tracking-widest pl-1 mb-3 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />
                  Conversation ({ticket.responses?.length || 0})
                </h3>
                <div className="space-y-3">
                  {!ticket.responses?.length ? (
                    <div className="p-4 rounded-xl bg-slate-50/50 dark:bg-[#002147]/50 border border-dashed border-slate-200 dark:border-[#1a3884]/20 text-center">
                      <p className="text-slate-400 dark:text-gray-500 text-sm font-semibold">No responses yet — our team will respond soon.</p>
                    </div>
                  ) : (
                    ticket.responses.map((r, i) => (
                      <motion.div key={r._id || i}
                        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.05 }}
                        className="p-4 rounded-xl bg-slate-50 dark:bg-[#1a3884]/10 border border-slate-100 dark:border-[#1a3884]/20"
                      >
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <div className="w-6 h-6 rounded-full bg-[#4f7dff]/20 flex items-center justify-center flex-shrink-0">
                            <span className="text-[10px] text-[#4f7dff] font-bold">
                              {(r.respondedBy?.fullName || 'S').charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <span className="text-sm text-slate-900 dark:text-white font-semibold">
                            {r.respondedBy?.fullName || 'Support Team'}
                          </span>
                          <span className="text-xs text-slate-400 dark:text-gray-500">{formatDate(r.respondedAt)}</span>
                        </div>
                        <p className="text-slate-700 dark:text-gray-300 text-sm whitespace-pre-wrap pl-0 sm:pl-8 leading-relaxed">{r.message}</p>
                      </motion.div>
                    ))
                  )}
                </div>
              </div>

              {/* Reply section — only for open/in-progress */}
              {ticket.status !== 'closed' && ticket.status !== 'resolved' && (
                <div className="pt-4 border-t border-slate-100 dark:border-[#1a3884]/20">
                  <h3 className="text-xs font-bold text-slate-400 dark:text-gray-500 uppercase tracking-widest pl-1 mb-3 flex items-center gap-2">
                    <MessageSquare className="w-4 h-4" /> Add a message
                  </h3>
                  <div className="relative">
                    <textarea
                      value={replyMessage}
                      onChange={(e) => setReplyMessage(e.target.value)}
                      placeholder="Add more details or reply to support..."
                      rows={2}
                      className="w-full px-3 py-2 rounded-xl bg-slate-50 dark:bg-[#002147] border border-slate-200 dark:border-[#1a3884]/30 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-gray-500 focus:ring-4 focus:ring-[#1a3884]/20 focus:border-[#4f7dff] focus:outline-none transition-all resize-none pr-12 text-sm font-medium"
                    />
                    <button
                      onClick={handleReply}
                      disabled={isSubmitting || !replyMessage.trim()}
                      className="absolute top-1/2 -translate-y-1/2 right-2 p-1.5 rounded-lg bg-[#1a3884] hover:bg-[#4f7dff] text-white transition-colors disabled:opacity-50 flex items-center justify-center"
                    >
                      {isSubmitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        : submitSuccess ? <CheckCircle className="w-3.5 h-3.5" />
                          : <Send className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                  <p className="text-xs text-slate-400 dark:text-gray-500 mt-2">Your message will be sent to the support team.</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-100 dark:border-[#1a3884]/20 bg-slate-50 dark:bg-[#002147]/50 flex justify-between items-center">
          <span className="text-xs text-slate-400 dark:text-gray-500">Last updated: {formatDate(ticket.updatedAt)}</span>
          <button onClick={onClose}
            className="px-4 py-2 rounded-lg border border-slate-200 dark:border-[#1a3884]/50 text-slate-500 dark:text-gray-400 hover:text-slate-900 dark:hover:text-white hover:border-[#1a3884] dark:hover:border-white transition-colors">
            Close
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ─── Main Page ───────────────────────────────────────────────────────────────
const MyITSupport = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('create');
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [successTicket, setSuccessTicket] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [errors, setErrors] = useState({});
  const [fromChatbot, setFromChatbot] = useState(false);
  const [formData, setFormData] = useState({
    subject: '', description: '', category: '', priority: 'medium'
  });
  const [uploading, setUploading] = useState(false);
  const [attachments, setAttachments] = useState([]);

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    if (attachments.length + files.length > 20) {
      toast.error('Maximum 20 attachments allowed');
      return;
    }

    setUploading(true);
    try {
      const uploadedAttachments = [];
      for (const file of files) {
        if (file.size > 5 * 1024 * 1024) {
          toast.error(`File ${file.name} exceeds 5MB size limit`);
          continue;
        }

        const uploadData = new FormData();
        uploadData.append('image', file);
        const { data } = await api.post('/upload/image', uploadData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        if (data.success) {
          uploadedAttachments.push({
            filename: data.data.publicId || data.data.fileName,
            originalName: data.data.fileName,
            name: data.data.fileName,
            path: data.data.url,
            url: data.data.url,
            mimetype: data.data.resourceType ? `${data.data.resourceType}/${data.data.format || 'unknown'}` : file.type,
            size: data.data.fileSize
          });
        }
      }
      if (uploadedAttachments.length > 0) {
        setAttachments(prev => [...prev, ...uploadedAttachments]);
        toast.success(`Successfully uploaded ${uploadedAttachments.length} file(s)`);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to upload attachment(s)');
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  // Pre-fill form from chatbot escalation state or auto-open ticket from notification link
  useEffect(() => {
    const state = location.state;
    if (state?.messages && Array.isArray(state.messages) && state.messages.length > 0) {
      const conversationText = state.messages
        .filter(m => m.role !== undefined)
        .map(m => {
          const sender = m.role === 'user' ? 'Me' : 'Support Bot';
          return `[${sender}]: ${m.content}`;
        })
        .join('\n\n');

      setFormData(prev => ({
        ...prev,
        description: `--- Chat Conversation ---\n\n${conversationText}\n\n--- Additional Details ---\n`,
      }));
      setFromChatbot(true);
      // Clear state so refresh doesn't re-populate
      window.history.replaceState({}, document.title);
    } else if (state?.openTicketId) {
      setActiveTab('history');
      setSelectedTicket({ _id: state.openTicketId });
      // Clear state so refresh doesn't re-populate
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => { if (activeTab === 'history') fetchTickets(); }, [activeTab]);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/tickets/my-tickets');
      if (data.success) setTickets(data.data);
    } catch (err) {
      if (!navigator.onLine) {
        toast.error('No internet connection. Please check your network and try again.');
      } else {
        toast.error('Failed to load tickets');
      }
    }
    finally { setLoading(false); }
  };

  const validate = () => {
    const e = {};
    if (!formData.subject.trim()) e.subject = 'Title is required';
    if (!formData.description.trim()) e.description = 'Description is required';
    if (!formData.category) e.category = 'Please select a category';
    setErrors(e);
    return !Object.keys(e).length;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const submitData = {
        ...formData,
        attachments: attachments
      };
      const { data } = await api.post('/tickets', submitData);
      if (data.success) {
        setSuccessTicket(data.data);
        setFormData({ subject: '', description: '', category: '', priority: 'medium' });
        setAttachments([]);
        toast.success('Ticket submitted!');
        setTimeout(() => { setSuccessTicket(null); setActiveTab('history'); }, 2000);
      }
    } catch (err) {
      if (!navigator.onLine) {
        toast.error('No internet connection. Please check your network and try again.');
      } else {
        toast.error('Failed to submit ticket');
      }
    }
    finally { setSubmitting(false); }
  };

  const statusColor = (s) => STATUS_CONFIG[s]?.color || STATUS_CONFIG.open.color;

  return (
    <div className="space-y-6">
      <div className="w-full space-y-6">

        {/* Header */}
        <div className="border border-slate-200 dark:border-[#1a3884]/30 rounded-2xl px-6 py-4 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white dark:bg-[#002147] mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="p-2 rounded-xl border border-gray-200 dark:border-[#1a3884]/30 hover:bg-gray-50 dark:hover:bg-[#1a3884]/20 text-gray-500 transition-colors"
              title="Back to Dashboard"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-300" />
            </button>
            <div className="space-y-0.5 text-left">
              <h1 className="text-[22px] font-black text-[#1a3884] dark:text-[#4f7dff] tracking-tight">IT Support</h1>
              <div className="flex items-center gap-1.5 text-[13px] font-medium text-gray-500 dark:text-gray-400">
                <LifeBuoy className="w-4 h-4 text-[#1a3884]/60 dark:text-[#4f7dff]/60" />
                Raise and track your IT support requests
              </div>
            </div>
          </div>
          <div className="flex p-1 bg-gray-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl">
            <button onClick={() => setActiveTab('create')}
              className={`px-4 py-2 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-all ${activeTab === 'create' ? 'bg-[#1a3884] text-white shadow-md' : 'text-slate-500 dark:text-gray-400 hover:text-slate-900 dark:hover:text-white'}`}>
              <Plus size={16} /> New Ticket
            </button>
            <button onClick={() => setActiveTab('history')}
              className={`px-4 py-2 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-all ${activeTab === 'history' ? 'bg-[#1a3884] text-white shadow-md' : 'text-slate-500 dark:text-gray-400 hover:text-slate-900 dark:hover:text-white'}`}>
              <History size={16} /> My Tickets
            </button>
          </div>
        </div>

        <AnimatePresence mode="wait">
          {activeTab === 'create' ? (
            <motion.div key="create" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} className="w-full">

              {/* ITSM success banner */}
              {successTicket?.itsmTicketNumber && (
                <div className="mb-6 p-4 rounded-xl bg-[#1a3884]/10 border border-[#1a3884]/30">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-[#1a3884]" />
                    <span className="text-sm font-semibold text-[#1a3884] dark:text-[#4f7dff]">Ticket submitted successfully</span>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-gray-400 mt-1">
                    ITSM reference:{' '}
                    <span className="font-mono text-[#1a3884] dark:text-[#4f7dff] font-bold">{successTicket.itsmTicketNumber}</span>
                  </p>
                </div>
              )}

              <div className="border border-gray-100 dark:border-[#1a3884]/20 rounded-[1.25rem] p-6 shadow-sm bg-white dark:bg-[#002147] w-full">
                <form onSubmit={handleSubmit} className="space-y-8">

                  {/* Title */}
                  <div className="space-y-3">
                    <label className="block text-sm font-bold text-slate-700 dark:text-white">
                      Title <span className="text-red-400">*</span>
                    </label>
                    <input type="text" value={formData.subject}
                      onChange={e => setFormData({ ...formData, subject: e.target.value })}
                      placeholder="Brief summary of your issue"
                      className={`w-full px-4 py-3 rounded-xl bg-slate-50 dark:bg-[#002147]/40 border ${errors.subject ? 'border-red-500' : 'border-slate-200 dark:border-[#1a3884]/30'} text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-gray-500 focus:ring-4 focus:ring-[#1a3884]/10 focus:border-[#4f7dff] focus:outline-none transition-all duration-300 font-medium h-[46px]`}
                    />
                    {errors.subject && <p className="mt-1 text-sm text-red-500">{errors.subject}</p>}
                  </div>

                  {/* Category */}
                  <div className="space-y-3">
                    <label className="block text-sm font-bold text-slate-700 dark:text-white">
                      Category <span className="text-red-400">*</span>
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch">
                      {CATEGORIES.map(cat => (
                        <button key={cat.value} type="button"
                          onClick={() => setFormData({ ...formData, category: cat.value })}
                          className={`p-5 rounded-[1rem] border text-left transition-all hover:shadow-md hover:-translate-y-0.5 w-full h-full flex flex-col justify-start ${formData.category === cat.value ? 'border-[#1a3884] dark:border-[#4f7dff] bg-[#1a3884]/10 dark:bg-[#1a3884]/20 ring-1 ring-[#1a3884] dark:ring-[#4f7dff]' : 'bg-white dark:bg-white/5 border-gray-150 dark:border-[#1a3884]/30 hover:border-[#1a3884]'}`}
                        >
                          <span className={`font-semibold text-sm block ${formData.category === cat.value ? 'text-[#1a3884] dark:text-[#4f7dff]' : 'text-slate-900 dark:text-white'}`}>{cat.label}</span>
                          <p className="text-slate-500 dark:text-gray-400 text-xs mt-2 leading-relaxed">{cat.description}</p>
                        </button>
                      ))}
                    </div>
                    {errors.category && <p className="mt-2 text-sm text-red-400">{errors.category}</p>}
                  </div>

                  {/* Priority */}
                  <div className="space-y-3">
                    <label className="block text-sm font-bold text-slate-700 dark:text-white">Priority</label>
                    <div className="flex flex-wrap gap-4">
                      {PRIORITIES.map(pri => (
                        <button key={pri.value} type="button"
                          onClick={() => setFormData({ ...formData, priority: pri.value })}
                          className={`px-5 py-2.5 rounded-xl border transition-all flex-1 sm:flex-none text-sm font-bold h-[42px] flex items-center justify-center ${formData.priority === pri.value ? pri.color : 'bg-white dark:bg-white/5 border-slate-200 dark:border-[#1a3884]/30 text-slate-500 dark:text-gray-400 hover:border-[#1a3884]/50'}`}
                        >
                          {pri.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Attachments Dropzone */}
                  <div className="space-y-3">
                    <label className="block text-sm font-bold text-slate-700 dark:text-white">
                      Attachments <span className="text-xs text-gray-500 font-normal">(Optional)</span>
                    </label>

                    <div className="relative">
                      <input
                        type="file"
                        multiple
                        accept=".pdf,.doc,.docx,image/*"
                        onChange={handleFileChange}
                        disabled={uploading}
                        className="hidden"
                        id="screenshot-upload"
                      />
                      <label
                        htmlFor="screenshot-upload"
                        className={`flex flex-col items-center justify-center w-full h-28 border-2 border-dashed rounded-xl cursor-pointer transition-colors ${uploading
                          ? 'border-blue-400 bg-blue-50/5 dark:bg-blue-950/10'
                          : 'border-slate-200 dark:border-[#1a3884]/30 bg-slate-50 dark:bg-[#002147]/10 hover:bg-slate-100 dark:hover:bg-[#002147]/20'
                          }`}
                      >
                        {uploading ? (
                          <div className="flex items-center gap-2 text-blue-500 font-semibold">
                            <Loader2 className="w-5 h-5 animate-spin" />
                            <span>Uploading...</span>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center justify-center p-4 text-center">
                            <Paperclip className="w-6 h-6 text-slate-400 dark:text-gray-500 mb-1 rotate-45" />
                            <span className="text-sm font-semibold text-slate-600 dark:text-gray-300">Upload Screenshot</span>
                            <span className="text-xs text-slate-400 dark:text-gray-500 mt-1 font-normal">PNG, JPG, WEBP (Max 5MB)</span>
                          </div>
                        )}
                      </label>
                    </div>

                    {attachments.length > 0 && (
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
                        {attachments.map((file, index) => {
                          const isImage = file.mimetype?.startsWith('image/') || 
                                          /\.(jpg|jpeg|png|gif|webp)$/i.test(file.url || file.originalName || '');
                          return (
                            <div
                              key={index}
                              className="flex items-center justify-between p-3 bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl font-medium"
                            >
                              <div className="flex items-center gap-3 min-w-0">
                                {isImage ? (
                                  <img
                                    src={file.url}
                                    alt="Screenshot"
                                    className="w-10 h-10 object-cover rounded-lg border border-slate-200 flex-shrink-0"
                                  />
                                ) : (
                                  <Paperclip className="w-6 h-6 text-[#1a3884] flex-shrink-0" />
                                )}
                                <div className="min-w-0">
                                  <p className="text-xs font-semibold text-slate-700 dark:text-white truncate">
                                    {file.originalName}
                                  </p>
                                  <p className="text-[10px] text-slate-500">Uploaded</p>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeAttachment(index)}
                                className="p-1.5 rounded-lg text-rose-500 hover:bg-rose-500/10 transition-colors"
                                title="Remove Screenshot"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Description */}
                  <div className="space-y-3">
                    <label className="block text-sm font-bold text-slate-700 dark:text-white">
                      Description <span className="text-red-400">*</span>
                    </label>

                    {/* Chat conversation included banner */}
                    {fromChatbot && (
                      <div className="flex items-start gap-3 mb-3 px-4 py-3 rounded-xl bg-[#1a3884]/10 border border-[#1a3884]/25">
                        <MessageSquare className="w-4 h-4 text-[#4f7dff] mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-semibold text-[#1a3884] dark:text-blue-300">Chat conversation included</p>
                          <p className="text-xs text-slate-500 dark:text-gray-400 mt-0.5">Your chat history has been automatically attached to this ticket.</p>
                        </div>
                      </div>
                    )}

                    <textarea value={formData.description}
                      onChange={e => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Describe your issue in detail..."
                      rows={fromChatbot ? 10 : 5}
                      className={`w-full px-4 py-3 rounded-xl bg-slate-50 dark:bg-[#002147] border ${errors.description ? 'border-red-500' : 'border-slate-200 dark:border-[#1a3884]/30'} text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-gray-500 focus:ring-4 focus:ring-[#1a3884]/10 focus:border-[#4f7dff] focus:outline-none transition-all duration-300 resize-none font-mono text-sm leading-relaxed`}
                    />
                    {errors.description && <p className="mt-1 text-sm text-red-500">{errors.description}</p>}
                  </div>

                  <div className="flex justify-end pt-2">
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={submitting}
                      icon={submitting ? Loader2 : Send}
                      className="border-none bg-[#1a3884] hover:bg-[#132A55] text-white rounded-xl shadow-md px-5 py-2.5"
                    >
                      {submitting ? 'Submitting...' : 'Submit Ticket'}
                    </Button>
                  </div>
                </form>
              </div>
            </motion.div>

          ) : (
            <motion.div key="history" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
              {loading ? (
                <div className="py-20 flex flex-col items-center justify-center text-slate-400">
                  <Loader2 className="w-10 h-10 animate-spin mb-4 text-[#4f7dff]" />
                  <p>Loading your tickets...</p>
                </div>
              ) : tickets.length === 0 ? (
                <div className="py-20 text-center bg-white dark:bg-white/5 rounded-2xl border border-slate-200 dark:border-white/10">
                  <LifeBuoy className="w-12 h-12 text-slate-300 dark:text-gray-600 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">No Tickets Yet</h3>
                  <p className="text-slate-500 dark:text-gray-400 mb-6">You haven't submitted any IT support requests.</p>
                  <button onClick={() => setActiveTab('create')}
                    className="px-6 py-2 bg-[#1a3884] text-white rounded-lg font-bold hover:scale-105 transition-transform">
                    Raise a Ticket
                  </button>
                </div>
              ) : (
                tickets.map(ticket => (
                  <div key={ticket._id} onClick={() => setSelectedTicket(ticket)}
                    className="bg-white dark:bg-[#002147] p-6 rounded-[1.25rem] border border-gray-100 dark:border-[#1a3884]/20 hover:shadow-md transition-all group cursor-pointer relative overflow-hidden text-left shadow-sm">
                    <div className="flex flex-col sm:flex-row justify-between items-start gap-3 mb-4">
                      <div>
                        <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                          <span className="text-xs font-mono text-slate-400 dark:text-gray-500 uppercase tracking-widest">
                            #{ticket.ticketId || ticket._id?.slice(-6)}
                          </span>
                          {ticket.itsmTicketNumber && (
                            <span className="px-2 py-0.5 text-xs rounded-full border bg-[#1a3884]/20 text-[#1a3884] dark:text-[#4f7dff] border-[#1a3884]/30 font-mono flex items-center gap-1">
                              <span className="w-1.5 h-1.5 rounded-full bg-[#1a3884] dark:bg-[#4f7dff] inline-block" />
                              {ticket.itsmTicketNumber}
                            </span>
                          )}
                        </div>
                        <h3 className="text-lg font-bold text-slate-900 dark:text-white group-hover:text-[#4f7dff] transition-colors tracking-tight">
                          {ticket.subject || ticket.title}
                        </h3>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-bold border flex-shrink-0 ${statusColor(ticket.status)} capitalize`}>
                        {ticket.status}
                      </span>
                    </div>
                    <p className="text-slate-600 dark:text-gray-400 text-sm mb-4 line-clamp-2 leading-relaxed">{ticket.description}</p>
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-slate-500 dark:text-gray-400 font-medium">
                      <span className="flex items-center gap-1.5"><Clock size={14} />{new Date(ticket.createdAt).toLocaleDateString()}</span>
                      {ticket.responses?.length > 0 && (
                        <span className="flex items-center gap-1.5 text-[#4f7dff]">
                          <MessageSquare size={14} /> {ticket.responses.length} Responses
                        </span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Ticket Detail Modal */}
      <AnimatePresence>
        {selectedTicket && (
          <TicketDetailModal
            ticket={selectedTicket}
            onClose={() => setSelectedTicket(null)}
            onUpdate={(updated) => {
              setTickets(prev => prev.map(t => t._id === updated._id ? updated : t));
              setSelectedTicket(updated);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default MyITSupport;
